import {ID} from '@wandb/weave/common/util/id';
import React, {useCallback, useMemo, useRef} from 'react';
import {twMerge} from 'tailwind-merge';

import {XAxisFormatValues} from '../../../../generated/graphql';
import {type SettingType} from '../../../../services/analytics/workspaceSettingsEvents';
import {
  calculateIsSettingEnabled,
  type SettingOverrideCounts,
} from '../../../../state/views/workspaceSettings/settingOverrideUtils';
import {useIsNgwEnabled} from '../../../../util/rampFeatureFlags/rampFeatureFlags';
import {globalXAxisOptions} from '../../../../util/runhelpers';
import {MetricSearchSelect} from '../../../MetricSearch/MetricSearchSelect';
import {useKeyInfoQueryContext} from '../../../MultiRunWorkspace/KeyInfoQueryContext';
import {useMaybeWorkspaceRefsContext} from '../../../Workspace/WorkspaceRefsContext';
import {ClearSettingsRows} from '../components/ClearSettingsRow';
import {ResetSettingButton} from '../components/ResetSettingButton';
import {SettingIndentedRow} from '../components/SettingIndentedRow';
import {SettingLabel} from '../components/SettingLabel';
import {SettingSelect} from '../components/SettingSelect';
import {useClearSettingRows} from '../hooks/useClearSettingRows';
import {type ClearSettingFnType} from '../hooks/useSettingOverrides';
import {
  type LinePlotSettings,
  type SettingLevel,
  type SettingSelectOptionType,
} from '../types';
import {toXAxisOption, xAxisLabel} from '../utils';
import {
  DEFAULT_X_AXIS_OPTION,
  DEFAULT_X_AXIS_OPTION_VALUES,
} from './linePlotDefaults';
import {XAxisFormat, xAxisIsBuiltin} from './XAxisFormat';
import {XAxisRange} from './XAxisRange';

const EMPTY_X_AXIS_SETTINGS = {
  xAxis: undefined,
  xAxisMin: undefined,
  xAxisMax: undefined,
  xLogScale: undefined,
  xAxisFormat: undefined,
};

// Only numeric metrics are valid x-axis choices. metricSearch reports a `types`
// array per metric; this filters the results client-side (see useMetricKeySearch).
const X_AXIS_METRIC_TYPES = ['number'];
const X_AXIS_METRIC_RESULT_LIMIT = 100;

// Built-in x-axis keys the metric-search API does not return; prepended to the
// options so they stay selectable.
const RESERVED_X_AXIS_OPTIONS = DEFAULT_X_AXIS_OPTION_VALUES.map(key => ({
  value: key,
  label: xAxisLabel(key),
}));

export type LinePlotXAxisProps = {
  clearSetting: ClearSettingFnType;

  disabled?: boolean;
  /**
   * These are the values that are displayed to the user. We want to show the
   * inherited values when relevant.
   */
  displayValues: {
    xAxis: string;
    xAxisMax: number | undefined;
    xAxisMin: number | undefined;
    xLogScale: boolean; // we need a value to feed into the toggle comp
    xAxisFormat?: XAxisFormatValues;
  };
  numOverrides?: SettingOverrideCounts;
  settingLevel: SettingLevel;
  /**
   * These are the values that match in the view spec depending on the setting level.
   */
  values: {
    xAxis?: string | undefined;
    xAxisMax?: number | null | undefined;
    xAxisMin?: number | null | undefined;
    xLogScale?: boolean | undefined; // this can be undefined to indicate user hasn't touched the setting
    xAxisFormat?: XAxisFormatValues;
  };
  useXAxisOptions?: () => SettingSelectOptionType[];
  trackSetting: (setting: SettingType, value: string | undefined) => void;
  updateSetting: (settings: Partial<LinePlotSettings>) => void;
};

export const LinePlotXAxis = ({
  clearSetting,
  disabled,
  numOverrides,
  settingLevel,
  trackSetting,
  updateSetting,
  useXAxisOptions,
  values,
  displayValues,
}: LinePlotXAxisProps) => {
  const ref = useRef({
    xAxisSelectorId: 'x-axis-selector-' + ID(),
  });

  useXAxisOptions = useXAxisOptions ?? useWorkspaceXAxisOptions;
  const xAxisOptions = useXAxisOptions();

  const isNgwLinePlotPickerEnabled = useIsNgwEnabled();
  const workspaceRefs = useMaybeWorkspaceRefsContext();
  const shouldUseMetricApi =
    isNgwLinePlotPickerEnabled && workspaceRefs != null;

  const xAxisValue = useMemo(() => {
    // In metric-search mode we only care about workspace values for now (until we apply ngw in reports), so the below check doesn't apply.
    if (shouldUseMetricApi && displayValues.xAxis != null) {
      return toXAxisOption(displayValues.xAxis);
    }
    // X axis options in a workspace vs reports are different.
    // If a user tries to create a report from the workspace page,
    // we need to check if the currently selected xAxis is valid in reports.
    const isValidXAxis = (xAxisOptions ?? []).some(
      opt => opt.value === displayValues.xAxis
    );
    if (isValidXAxis && displayValues.xAxis != null) {
      return toXAxisOption(displayValues.xAxis);
    }
    return DEFAULT_X_AXIS_OPTION;
  }, [shouldUseMetricApi, displayValues.xAxis, xAxisOptions]);

  const updateXAxis = useCallback(
    (value: string) => {
      const xAxisFormat =
        value === '_timestamp'
          ? XAxisFormatValues.Datetime
          : XAxisFormatValues.Number;
      updateSetting({xAxis: value, xAxisFormat});
      trackSetting('xAxis', value);
    },
    [updateSetting, trackSetting]
  );

  const onXAxisChange = useCallback(
    (selected: SettingSelectOptionType | null) => {
      if (selected != null) {
        updateXAxis(selected.value.toString());
      }
    },
    [updateXAxis]
  );

  const clearSettingRows = useClearSettingRows({
    clearSetting,
    emptySettings: EMPTY_X_AXIS_SETTINGS,
    numOverrides,
    settingKey: 'xAxis',
    settingLevel,
  });

  const xAxisLabelNode = (
    <SettingLabel
      text="X axis"
      // oxlint-disable-next-line react/refs
      htmlFor={ref.current.xAxisSelectorId}
      className="shrink-0"
    />
  );
  const xAxisResetButton = (
    <ResetSettingButton
      isEnabled={calculateIsSettingEnabled('xAxis', values)} // important that we are looking at value is spec at settingLevel rather than the displayValues which may be inherited from parent
      onButtonClick={() =>
        clearSetting(settingLevel, 'xAxis', EMPTY_X_AXIS_SETTINGS)
      }
      settingLevel={settingLevel}
    />
  );
  return (
    <div
      className={twMerge('flex flex-col', 'gap-8')}
      data-test="x-axis-setting">
      {settingLevel !== 'panel' ? (
        <div className="flex w-full">
          {xAxisLabelNode}
          <div className="flex w-full flex-row gap-8">
            {shouldUseMetricApi ? (
              // The react-select counterpart of the metric search picker, so
              // the x-axis keeps SettingSelect's widget under NGW.
              <MetricSearchSelect
                ariaLabel="X axis"
                className="flex grow flex-col gap-4"
                dataTest="x-axis-metric-search"
                ddActionName="X axis"
                labelForValue={xAxisLabel}
                onChange={updateXAxis}
                placeholder="X axis"
                reservedOptions={RESERVED_X_AXIS_OPTIONS}
                resultLimit={X_AXIS_METRIC_RESULT_LIMIT}
                types={X_AXIS_METRIC_TYPES}
                value={xAxisValue.value}
              />
            ) : (
              <SettingSelect
                // oxlint-disable-next-line react/refs
                id={ref.current.xAxisSelectorId}
                ddActionName="X axis"
                className="grow"
                options={xAxisOptions}
                value={xAxisValue}
                onChange={onXAxisChange}
              />
            )}
            {xAxisResetButton}
          </div>
        </div>
      ) : null}
      <div className="flex">
        <SettingIndentedRow>
          {!xAxisIsBuiltin(xAxisValue.value) ? (
            <XAxisFormat
              trackSetting={trackSetting}
              updateSetting={updateSetting}
              xAxisFormat={displayValues.xAxisFormat}
            />
          ) : null}
          <XAxisRange
            disabled={disabled}
            trackSetting={trackSetting}
            settingLevel={settingLevel}
            updateSetting={updateSetting}
            xAxisMax={displayValues.xAxisMax}
            xAxisMin={displayValues.xAxisMin}
            xLogScale={displayValues.xLogScale}
            labelRequired={settingLevel !== 'panel'}
          />
        </SettingIndentedRow>
      </div>
      <ClearSettingsRows rows={clearSettingRows} />
    </div>
  );
};

export const useWorkspaceXAxisOptions = () => {
  const {
    keyInfoQuery: {historyKeyInfo},
  } = useKeyInfoQueryContext();
  const historyKeyXAxis = useMemo(
    () => globalXAxisOptions(historyKeyInfo),
    [historyKeyInfo]
  );

  const xValues = useMemo(() => {
    return DEFAULT_X_AXIS_OPTION_VALUES.concat(historyKeyXAxis).map(
      toXAxisOption
    );
  }, [historyKeyXAxis]);

  return xValues;
};
